import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import FeeSchema, { FeeParams, FeeJSON } from "_shared/fee-schedules/categories/fees/fee-schema";
import { formatCurrency } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";

export default class Fee {
  public static get EXAMPLE(): Fee {
    return Fee.create({rate: Money.EXAMPLE, unit: ''});
  }

  public static create(values: FeeParams): Fee {
    const parsed = FeeSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid Fee parameters.");
    }
    return new Fee(parsed);
  }

  public static fromJSON(json: FeeJSON): Fee {
    if (isNullOrUndefined(json)) return json;
    const params = FeeSchema.deserialize(json);
    return new Fee(params);
  }

  /**
   * Whether a unit means an hourly rate, in any of the spellings the field accepts.
   *
   * Static because the callers that need it most hold a raw unit from a form rather than a `Fee`,
   * and an hourly rate is the one kind that has to name who is billing it (issue #4489) — a rule
   * two places now enforce, which is one definition of "hourly" too few to have twice.
   */
  public static isHourlyUnit(unit: string | null | undefined): boolean {
    return ["hour", "hours", "hr", "hrs", "hourly"].includes(unit?.trim().toLowerCase() ?? "");
  }

  public readonly rate: Money;
  public readonly unit: string | null;

  public get isFixed(): boolean {
    return !this.unit;
  }

  public get isHourly(): boolean {
    return Fee.isHourlyUnit(this.unit);
  }

  public get feeString(): string {
    if (!this.rate) return '';
    return this.unit ? `${this.rate.toString()} / ${this.unit}` : `${this.rate.toString()} fixed`;
  }

  public constructor(params: FeeParams) {
    this.rate = Money.create(params.rate);
    this.unit = params.unit;
  }

  public isEqualTo(other: Fee | null): boolean {
    if (!other) return false;
    return (
      ((!this.rate && !other.rate) || !!this.rate?.isEqualTo(other.rate)) &&
      (this.unit === other.unit)
    );
  }

  public clone(): Fee {
    return new Fee({
      rate: this.rate ? this.rate.clone() : this.rate,
      unit: this.unit,
    });
  }

  public toJSON(): FeeJSON {
    return FeeSchema.serialize(this);
  }

  public toString(): string {
    if (!this.rate) return '';
    return `${formatCurrency(this.rate)} ${this.unit ? '/ ' + this.unit : 'fixed'}`;
  }
}
